<script setup>

import {computed, onMounted, ref, watch} from "vue";
import { useNotificationStore } from '@/stores/NotificationStore.js'
import FrostedBar from "../../components/layout/pageHeadings/frostedBar.vue";
const notificationStore = useNotificationStore()

const props = defineProps({
    showPreview: {
        type: Boolean,
        required: false,
        default: true
    },
});

const settings = ref(null)
const websiteHeaderTheme = ref(null)
const customerAreaHeaderTheme = ref(null)
const availableThemes = ref([])
const initialLanguage = ref(null)
const storeSettingsState = ref('');
const previewImageSrc = ref(null)
const previewFaviconSrc = ref(null)
const documentRoot = document.documentElement;
const logoFile = ref(null)
const previewingHeader = ref(false)
const faviconFile = ref(null)
const showFontWarning = ref(false)
const activeTab = ref('colors')

const updateDesignCssVariables = () => {
    documentRoot.style.setProperty("--whitelabel-rounding", settings.value.borderRoundness);
    documentRoot.style.setProperty("--whitelabel-navigation-layout", settings.value.navigationLayout);
    documentRoot.style.setProperty("--whitelabel-glass-blur", settings.value.glassBlur);
    documentRoot.style.setProperty("--whitelabel-glass-opacity", settings.value.glassOpacity);
}

const updateColorCssVariables = () => {
    documentRoot.style.setProperty("--whitelabel-primary", settings.value.primaryColor);
    documentRoot.style.setProperty("--whitelabel-primary-light", settings.value.primaryColorLight);
    documentRoot.style.setProperty("--whitelabel-primary-contrast", settings.value.primaryForegroundColor);
    documentRoot.style.setProperty("--whitelabel-primary-contrast-light", settings.value.primaryForegroundColorLight);
}

const previewSettings = async () => {
    const lang = settings.value.language;
    const customCss = settings.value.customCss;
    const res = await axios.post(route('settings.appearance.preview'), settings.value);
    settings.value = res.data.settings;
    settings.value.language = lang
    updateColorCssVariables();
    settings.value.customCss = customCss;
}

const loadSettings = async () => {
    const res = await axios.get(route('settings.appearance.show'));
    settings.value = res.data.settings;
    availableThemes.value = res.data.available_themes;
    websiteHeaderTheme.value = res.data.website_header_theme;
    customerAreaHeaderTheme.value = res.data.customer_area_header_theme;

    initialLanguage.value = res.data.settings.language;
    previewImageSrc.value = settings.value.logoUrl

    updateColorCssVariables();
    setDocumentFonts(settings.value.fonts.heading.family, settings.value.fonts.body.family)

    // Check if font warning has been shown
    checkFontWarning()
}

const checkFontWarning = async () => {
    try {
        const response = await axios.get(route('mf.pref.get', { key: 'font-settings-warning-shown' }))
        if (response.data === 'false' || response.data === false) {
            showFontWarning.value = true
        }
    } catch (error) {
        console.error('Error checking font warning preference:', error)
    }
}

const dismissFontWarning = async () => {
    try {
        await axios.post(route('mf.pref.set', { key: 'font-settings-warning-shown' }), {
            value: 'true'
        })
        showFontWarning.value = false
    } catch (error) {
        console.error('Error saving font warning preference:', error)
    }
}

const storeSettings = async () => {
    storeSettingsState.value = 'waiting';
    if (logoFile.value) {
        const formData = new FormData();
        formData.append("logoFile", logoFile.value.file);

        const logoRes = await axios.post(route('settings.appearance.storeLogo'), formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            }
        });

        if (!logoRes.data.success) {
            storeSettingsState.value = '';
            notificationStore.addNotification({
                type: 'error',
                title: _mft('setting:appearance.uploadLogo.error'),
                dismissAfter: 0,
            })

            return;
        }
    }

    if (faviconFile.value) {
        const formData = new FormData();
        formData.append("faviconFile", faviconFile.value.file);

        const faviconRes = await axios.post(route('settings.appearance.storeFavicon'), formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            }
        });

        if (!faviconRes.data.success) {
            storeSettingsState.value = '';
            notificationStore.addNotification({
                type: 'error',
                title: _mft('setting:appearance.uploadFavicon.error'),
                dismissAfter: 0,
            })

            return;
        }
    }

    await previewSettings();
    const res = await axios.post(route('settings.appearance.store'), settings.value);

    if(res.data.reload) {
        location.reload();
    }

    if (res.data.success) {
        storeSettingsState.value = 'success';
    }

}

const closePreview = () => {
    previewingHeader.value = false
}

const navigationSmallPreviewClasses = computed(() => {
    return {
        'fullwidth': 'w-full',
        'limited': 'w-3/5',
        'floating': 'transform translate-y-2 w-3/5'
    }[settings.value.navigationLayout]
})

const navigationBigPreviewClasses = computed(() => {
    return {
        'fullwidth': 'w-full',
        'limited': 'w-2/3 mx-auto rounded-b-wl',
        'floating': 'transform translate-y-2 w-2/3 mx-auto rounded-wl'
    }[settings.value.navigationLayout]
})


const updateFiles = (files) => {
    logoFile.value = files[0]
    previewImageSrc.value = 'data:image/png;base64, ' + files[0]?.getFileEncodeBase64String()
    previewingHeader.value = true
}

const updateFaviconFiles = (files) => {
    faviconFile.value = files[0]

    previewFaviconSrc.value = 'data:image/png;base64, ' + files[0]?.getFileEncodeBase64String()
}

const removedFile = () => {
    setTimeout(function(){
        previewImageSrc.value = settings.value.logoUrl
        previewingHeader.value = false
    }, 100)
}

const setFontFamily = (target, family) => {
    if (!family) {
        return
    }
    settings.value.fonts[target].family = family

    setDocumentFonts(settings.value.fonts.heading.family, settings.value.fonts.body.family)
}

const setDocumentFonts = (headings, body) => {
    WebFont.load({
        google: {
            families: [headings + ':400,600,700', body + ':400,600,700']
        }
    });

    document.getElementById('pagebuilder-font').innerHTML = `
            .font-selector.body .font-selector-preview,
            .font-demo {
                font-family: ${body}, Open Sans;
                font-size: ${settings.value.fonts.body.size}px;
            }

            .font-demo h1,
            .font-demo h2,
            .font-demo h3,
            .font-demo h4,
            .font-demo h5,
            .font-demo h6,
            .font-selector.headings .font-selector-preview {
                font-family: ${headings}, Open Sans;
            }
        `
};



watch(() => settings.value, (oldVal, newVal) => {
    updateDesignCssVariables();
}, {deep: true});

// Watch for tab change to show warning only when fonts tab is active
watch(() => activeTab.value, (newTab) => {
    if (newTab === 'fonts') {
        checkFontWarning()
    }
});

onMounted(() => {
    loadSettings();
});

</script>

<template>
    <div v-if="settings">
        <div class="md:sticky top-2 z-topbar">
            <frosted-bar>
                <div class="flex items-center justify-between space-x-4">
                    <h1>{{ _mft('setting:domainNamePlural') }}</h1>
                    <div class="flex items-center justify-between space-x-4">
                        <button-success
                            @click="storeSettings"
                            :state="storeSettingsState"
                        >{{ _mft('setting:saveSettings') }}</button-success>
                    </div>
                </div>
            </frosted-bar>
        </div>
        <div>
            <div class="mt-12">
                <card-tabbed @changestatustab="activeTab = $event">
                    <card-tabbed-tab
                        :active="true"
                        title="Branding"
                        value="branding"
                    >
                        <div class="mb-4">

                            <input-label
                                :label="'Logo'"
                            ></input-label>

                            <filepond-upload
                                ref="filepondUpload"
                                :showPreview="true"
                                accepted-file-types="image/png,image/jpeg,image/jpg,image/gif"
                                :required="false"
                                max-file-size="8MB"
                                :label-idle="_mft('setting:appearance.uploadLogo.labelIdle')"
                                @updatefiles="updateFiles($event)"
                                @removedfile="removedFile($event)"
                            ></filepond-upload>

                            <button-base
                                v-if="previewImageSrc"
                                class="justify-center w-full mt-4"
                                size="sm"
                                @click="previewingHeader = !previewingHeader"
                            >
                                <span v-if="previewingHeader">
                                    {{ _mft('setting:appearance.hidePreview') }}
                                </span>
                                <span v-else>
                                    {{ _mft('setting:appearance.previewLogo') }}
                                </span>
                            </button-base>

                        </div>

                        <div class="mb-4">

                            <input-label
                                :label="'Favicon'"
                            ></input-label>

                            <filepond-upload
                                ref="filepondFaviconUpload"
                                :showPreview="true"
                                accepted-file-types="image/png,image/jpeg,image/jpg,image/gif"
                                :required="false"
                                max-file-size="8MB"
                                :label-idle="_mft('setting:appearance.uploadFavicon.labelIdle')"
                                @updatefiles="updateFaviconFiles($event)"
                                @removedfile=""
                            ></filepond-upload>

                            <p
                                class="text-xs text-gray-500"
                            >
                                {{ _mft('setting:appearance.favicon.instructions') }}
                            </p>

                            <div
                                v-if="previewFaviconSrc?.length > 40 || previewFaviconSrc?.includes('https://')"
                                class="pt-2 border-b mb-4 pl-2"
                            >
                                <div class="w-full max-w-48 h-8 rounded-t-lg bg-gray-200 py-1 px-2 flex space-x-2 items-center">
                                    <div>
                                        <img
                                            class="object-cover object-center w-4 h-4 aspect-square"
                                            :src="previewFaviconSrc"
                                        />
                                    </div>
                                    <span class="text-xs">{{ _mft('setting:appearance.faviconPreviewTabLabel') }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="mb-4">
                            <div class="grid gap-4 md:grid-cols-2">
                                <div>
                                    <input-label :label="_mft('setting:appearance.backgroundColor')"></input-label>
                                    <color-picker
                                        :color="settings.primaryColor"
                                        :show-palette="true"
                                        @colorChanged="color => settings.primaryColor = color"
                                    ></color-picker>
                                </div>
                                <div>
                                    <input-label :label="_mft('setting:appearance.textColor')"></input-label>
                                    <color-picker
                                        :color="settings.primaryForegroundColor"
                                        :show-palette="true"
                                        @colorChanged="color => settings.primaryForegroundColor = color"
                                    ></color-picker>
                                </div>
                            </div>
                            <button-base
                                class="justify-center w-full mt-2"
                                size="sm"
                                @click="previewSettings"
                            >{{ _mft('shared:preview') }}</button-base>
                        </div>
                    </card-tabbed-tab>
                    <card-tabbed-tab
                        title="Design"
                        value="design"
                    >
                        <div class="grid md:grid-cols-2 gap-8 lg:gap-x-24">
                            <div class="mb-4">
                                <input-option
                                    :label="_mft('setting:appearance.roundedCorners.label')"
                                    direction="vertical"
                                    :required="false"
                                    :options="[
                                        { label: _mft('setting:appearance.roundedCorners.none'), value: '0px' },
                                        { label: _mft('setting:appearance.roundedCorners.tiny'), value: '0.33rem' },
                                        { label: _mft('setting:appearance.roundedCorners.aBit'), value: '0.75rem' },
                                        { label: _mft('setting:appearance.roundedCorners.quite'), value: '1.35rem' },
                                        { label: _mft('setting:appearance.roundedCorners.very'), value: '1.95rem' },
                                    ]"
                                    v-model:content="settings.borderRoundness"
                                />
                            </div>
                            <div class="mb-4 group">
                                <div v-if="websiteHeaderTheme === 'default' || customerAreaHeaderTheme === 'default'">
                                    <input-option
                                        :label="_mft('setting:appearance.navigationStyle.title')"
                                        :required="false"
                                        direction="vertical"
                                        :options="[
                                            { label: _mft('setting:appearance.navigationStyle.wide'), value: 'fullwidth' },
                                            { label: _mft('setting:appearance.navigationStyle.limited'), value: 'limited' },
                                            { label: _mft('setting:appearance.navigationStyle.floating'), value: 'floating' },
                                        ]"
                                        v-model:content="settings.navigationLayout"
                                    />
                                    <div
                                        class="fixed top-0 left-0 right-0 hidden transition-transform transform -translate-y-full z-topbar group-focus-within:translate-y-0 group-hover:translate-y-0 lg:block"
                                        :class="previewingHeader === true ? 'translate-y-0' : ''"
                                        @mouseenter="closePreview"
                                        @click="closePreview"
                                    >
                                        <div
                                            class="transition-transform transform -translate-y-full group-focus-within:translate-y-0 group-hover:translate-y-0"
                                            :class="previewingHeader === true ? 'translate-y-0' : ''"
                                        >
                                            <div
                                                class="mx-auto transition-all delay-75 bg-white bg-opacity-glass backdrop-blur-glass"
                                                :class="navigationBigPreviewClasses"
                                            >
                                                <div class="flex items-center justify-between w-full h-20 px-4 lg:px-6">
                                                    <div
                                                        class="w-24 h-12 text-left"
                                                        :class="previewImageSrc ? '' : 'bg-wlPrimary'"
                                                    >
                                                        <img
                                                            v-if="previewImageSrc?.length > 40 || previewImageSrc?.includes('https://')"
                                                            class="object-contain object-left w-24 h-12"
                                                            :src="previewImageSrc"
                                                        />
                                                    </div>
                                                    <div class="flex items-center space-x-4">
                                                        <div class="w-12 h-4 bg-black"></div>
                                                        <div class="w-12 h-4 bg-black"></div>
                                                        <div class="w-12 h-4 bg-black"></div>
                                                        <div class="w-12 h-4 bg-black"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="relative flex justify-center w-full bg-gray-200 border shadow-md lg:hidden" style="padding-bottom: 50%;">
                                        <div
                                            class="bg-white h-4 transition-all flex justify-between items-center px-0.5"
                                            :class="navigationSmallPreviewClasses"
                                        >
                                            <div class="w-5 h-2 bg-wlPrimary"></div>
                                            <div class="flex items-center space-x-1">
                                                <div class="w-4 h-1 bg-black"></div>
                                                <div class="w-4 h-1 bg-black"></div>
                                                <div class="w-4 h-1 bg-black"></div>
                                                <div class="w-4 h-1 bg-black"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-span-2">
                                <h2 class="mb-4">{{ _mft('setting:appearance.theme.headerTheme') }}</h2>
                                <div class="grid grid-cols-2 gap-8">
                                    <header-theme-manager
                                        location="website"
                                    ></header-theme-manager>
                                    <header-theme-manager
                                        location="customer_area"
                                    ></header-theme-manager>
                                </div>
                            </div>
                        </div>
                    </card-tabbed-tab>
                    <card-tabbed-tab
                        title="Fonts"
                        value="fonts"
                    >
                        <!-- Font Settings Warning Banner -->
                        <div v-if="showFontWarning" class="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div class="flex items-start">
                                <div class="flex-shrink-0">
                                    <svg class="h-5 w-5 text-amber-400 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                                    </svg>
                                </div>
                                <div class="ml-3 flex-1">
                                    <h3 class="text-sm font-medium text-amber-800">
                                        Important: Font changes affect your entire website
                                    </h3>
                                    <div class="mt-2 text-sm text-amber-700">
                                        <p>
                                            Changing font settings will affect all pages, forms, and customer-facing content across your entire website.
                                            This includes font families and font sizes. Please preview your changes carefully before saving.
                                        </p>
                                    </div>
                                    <div class="mt-4">
                                        <button
                                            @click="dismissFontWarning"
                                            type="button"
                                            class="text-sm font-medium text-amber-800 hover:text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-amber-50 rounded px-2 py-1"
                                        >
                                            I understand, don't show this again
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="grid lg:grid-cols-2 mb-8 lg:mb-12">
                            <div class="lg:border-r lg:pr-8 lg:py-4 mb-4 lg:mb-0">
                                <input-label :label="_mft('setting:appearance.font.heading')"></input-label>
                                <block-editor-font-options
                                    class="w-full font-selector headings"
                                    :font="settings.fonts.heading.family"
                                    :family-only="false"
                                    @update:modelValue="setFontFamily('heading', $event)"
                                />
                            </div>
                            <div class="lg:pl-8 py-4">
                                <input-label :label="_mft('setting:appearance.font.body')"></input-label>
                                <block-editor-font-options
                                    class="w-full font-selector body"
                                    :font="settings.fonts.body.family"
                                    :family-only="false"
                                    @update:modelValue="setFontFamily('body', $event)"
                                />
                            </div>
                        </div>

                        <div class="mt-8">
                            <ui-tabbed>
                                <ui-tabbed-tab
                                    :active="true"
                                    title="Body size"
                                    value="font-size-body"
                                >
                                    <div class="space-y-8">
                                        <div class="grid md:grid-cols-4 items-start gap-8 lg:px-8">

                                            <div>
                                                <input-label label="Mobile" align="center"></input-label>
                                                <input-number
                                                    :show-step-ui="true"
                                                    min="8"
                                                    max="144"
                                                    class="!mb-0"
                                                    v-model:content="settings.fonts.body.sizes.mobile"
                                                ></input-number>
                                            </div>
                                            <div class="font-demo col-span-3 pt-5 flex items-center min-h-16">
                                                <p class="transition-all px-2" :style="{fontSize: settings.fonts.body.sizes.mobile+'px'}">
                                                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed hendrerit sapien eu consequat gravida.
                                                </p>
                                            </div>
                                        </div>

                                        <div class="grid md:grid-cols-4 items-start gap-8 lg:px-8">

                                            <div>
                                                <input-label label="Desktop" align="center"></input-label>
                                                <input-number
                                                    :show-step-ui="true"
                                                    min="8"
                                                    max="144"
                                                    class="!mb-0"
                                                    v-model:content="settings.fonts.body.sizes.desktop"
                                                ></input-number>
                                            </div>
                                            <div class="font-demo col-span-3 pt-5 flex items-center min-h-16">
                                                <p class="transition-all px-2" :style="{fontSize: settings.fonts.body.sizes.desktop+'px'}">
                                                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed hendrerit sapien eu consequat gravida.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </ui-tabbed-tab>
                                <ui-tabbed-tab
                                    title="Small heading size"
                                    value="font-size-small-heading"
                                >
                                    <div class="space-y-8">
                                        <div class="grid md:grid-cols-4 items-start gap-8 lg:px-8">

                                            <div>
                                                <input-label label="Mobile" align="center"></input-label>
                                                <input-number
                                                    :show-step-ui="true"
                                                    min="8"
                                                    max="144"
                                                    class="!mb-0"
                                                    v-model:content="settings.fonts.heading.sizes.mobile.h3"
                                                ></input-number>
                                            </div>
                                            <div class="font-demo col-span-3 pt-5 flex items-center min-h-16">
                                                <h3 class="transition-all px-2" :style="{fontSize: settings.fonts.heading.sizes.mobile.h3+'px'}">
                                                    Lorem ipsum
                                                </h3>
                                            </div>
                                        </div>

                                        <div class="grid md:grid-cols-4 items-start gap-8 lg:px-8">

                                            <div>
                                                <input-label label="Desktop" align="center"></input-label>
                                                <input-number
                                                    :show-step-ui="true"
                                                    min="8"
                                                    max="144"
                                                    class="!mb-0"
                                                    v-model:content="settings.fonts.heading.sizes.desktop.h3"
                                                ></input-number>
                                            </div>
                                            <div class="font-demo col-span-3 pt-5 flex items-center min-h-16">
                                                <h3 class="transition-all px-2" :style="{fontSize: settings.fonts.heading.sizes.desktop.h3+'px'}">
                                                    Lorem ipsum
                                                </h3>
                                            </div>
                                        </div>
                                    </div>
                                </ui-tabbed-tab>
                                <ui-tabbed-tab
                                    title="Medium heading size"
                                    value="font-size-medium-heading"
                                >
                                    <div class="space-y-8">
                                        <div class="grid md:grid-cols-4 items-start gap-8 lg:px-8">

                                            <div>
                                                <input-label label="Mobile" align="center"></input-label>
                                                <input-number
                                                    :show-step-ui="true"
                                                    min="8"
                                                    max="144"
                                                    class="!mb-0"
                                                    v-model:content="settings.fonts.heading.sizes.mobile.h2"
                                                ></input-number>
                                            </div>
                                            <div class="font-demo col-span-3 pt-5 flex items-center min-h-16">
                                                <h2 class="transition-all px-2" :style="{fontSize: settings.fonts.heading.sizes.mobile.h2+'px'}">
                                                    Lorem ipsum
                                                </h2>
                                            </div>
                                        </div>

                                        <div class="grid md:grid-cols-4 items-start gap-8 lg:px-8">

                                            <div>
                                                <input-label label="Desktop" align="center"></input-label>
                                                <input-number
                                                    :show-step-ui="true"
                                                    min="8"
                                                    max="144"
                                                    class="!mb-0"
                                                    v-model:content="settings.fonts.heading.sizes.desktop.h2"
                                                ></input-number>
                                            </div>
                                            <div class="font-demo col-span-3 pt-5 flex items-center min-h-16">
                                                <h2 class="transition-all px-2" :style="{fontSize: settings.fonts.heading.sizes.desktop.h2+'px'}">
                                                    Lorem ipsum
                                                </h2>
                                            </div>
                                        </div>
                                    </div>
                                </ui-tabbed-tab>
                                <ui-tabbed-tab
                                    title="Big heading size"
                                    value="font-size-big-heading"
                                >
                                    <div class="space-y-8">
                                        <div class="grid md:grid-cols-4 items-start gap-8 lg:px-8">

                                            <div>
                                                <input-label label="Mobile" align="center"></input-label>
                                                <input-number
                                                    :show-step-ui="true"
                                                    min="8"
                                                    max="144"
                                                    class="!mb-0"
                                                    v-model:content="settings.fonts.heading.sizes.mobile.h1"
                                                ></input-number>
                                            </div>
                                            <div class="font-demo col-span-3 pt-5 flex items-center min-h-16">
                                                <h1 class="transition-all px-2" :style="{fontSize: settings.fonts.heading.sizes.mobile.h1+'px'}">
                                                    Lorem ipsum
                                                </h1>
                                            </div>
                                        </div>

                                        <div class="grid md:grid-cols-4 items-start gap-8 lg:px-8">

                                            <div>
                                                <input-label label="Desktop" align="center"></input-label>
                                                <input-number
                                                    :show-step-ui="true"
                                                    min="8"
                                                    max="144"
                                                    class="!mb-0"
                                                    v-model:content="settings.fonts.heading.sizes.desktop.h1"
                                                ></input-number>
                                            </div>
                                            <div class="font-demo col-span-3 pt-5 flex items-center min-h-16">
                                                <h1 class="transition-all px-2" :style="{fontSize: settings.fonts.heading.sizes.desktop.h1+'px'}">
                                                    Lorem ipsum
                                                </h1>
                                            </div>
                                        </div>
                                    </div>
                                </ui-tabbed-tab>
                            </ui-tabbed>
                        </div>
                    </card-tabbed-tab>

                    <card-tabbed-tab
                        title="Advanced"
                        value="advanced"
                    >
                        <div class="mb-4">
                            <input-label :label="_mft('setting:appearance.language')"></input-label>

                            <div class="max-w-sm mt-1">
                                <button-bar
                                    size="sm"
                                    :options="[
                                        {name: 'en', label: 'English'},
                                        {name: 'no', label: 'Norsk'},
                                        {name: 'sv', label: 'Svenska'},
                                    ]"
                                    :selected-option-name="settings.language"
                                    @select="settings.language = $event.name"
                                />
                            </div>
                        </div>

                        <div class="mb-4">
                            <input-label :label="_mft('setting:appearance.customCss')"></input-label>

                            <css-editor
                                v-model="settings.customCss"
                            />
                        </div>
                    </card-tabbed-tab>
                </card-tabbed>
            </div>
            <div
                v-if="showPreview"
                class="col-span-2"
            >
                <div
                    class="grid gap-4 xl:grid-cols-2 xl:gap-8 font-demo max-w-4xl mx-auto mt-12 transition-all"
                    :style="{fontSize: settings.fonts.body.size + 'px'}"
                >
                    <div>
                        <h2>{{ _mft('setting:appearance.exampleHeading') }}</h2>
                        <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed hendrerit sapien eu consequat gravida. Curabitur eu velit vitae dui rutrum tempus. Nullam aliquam nibh risus, vitae laoreet libero mollis a. Nam convallis, nulla at placerat bibendum, lacus nisl ultrices augue, ut convallis turpis magna a metus. Nullam urna metus, vestibulum in velit quis, convallis maximus nisl. Donec a ultricies eros. Vivamus porttitor augue eu turpis iaculis porttitor.</p>
                        <div class="flex justify-end pb-4 space-x-2 md:pb-0">
                            <button-secondary>
                                {{ _mft('setting:appearance.exampleButton') }}
                            </button-secondary>
                            <button-primary>
                                {{ _mft('setting:appearance.exampleButton') }}
                            </button-primary>
                        </div>
                    </div>
                    <div>
                        <card-branded>
                            <template #top>
                                <div>
                                    <strong>{{ _mft('setting:appearance.exampleCard') }}</strong>
                                </div>
                            </template>
                            <template #header>
                                <h3>{{ _mft('setting:appearance.exampleHeading') }}</h3>
                            </template>
                            <template #body>
                                <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed hendrerit sapien eu consequat gravida. Curabitur eu velit vitae dui rutrum tempus. Nullam aliquam nibh risus, vitae laoreet libero mollis a. Nam convallis, nulla at placerat bibendum, lacus nisl ultrices augue, ut convallis turpis magna a metus. Nullam urna metus, vestibulum in velit quis, convallis maximus nisl. Donec a ultricies eros. Vivamus porttitor augue eu turpis iaculis porttitor.</p>
                                <div class="flex justify-end pb-4 space-x-2 md:pb-0">
                                    <button-secondary>
                                        {{ _mft('setting:appearance.exampleButton') }}
                                    </button-secondary>
                                    <button-primary>
                                        {{ _mft('setting:appearance.exampleButton') }}
                                    </button-primary>
                                </div>
                            </template>
                        </card-branded>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
.font-demo p {
    line-height: 1.3em;
}

.font-demo h1,
.font-demo h2,
.font-demo h3 {
    line-height: 1.3em;
}
</style>
